import { Button, Callout, Modal, theme } from '@agentisai/design-system';
import type { ArtifactListItem } from '@agentisai/shared-types';
import { useDeleteArtifact } from '../queries/useDeleteArtifact';

const message = {
  margin: 0,
  fontSize: theme.typography.fontSize.body,
  lineHeight: theme.typography.lineHeight.body,
  color: theme.color.neutral.body,
} as const;

const errorSlot = {
  marginTop: theme.space[3],
} as const;

export interface RemoveArtifactDialogProps {
  /** The workstream the artifact belongs to — keys the delete + its cache invalidation. */
  readonly workstreamId: string;
  /**
   * The artifact to remove. The parent mounts this dialog only while a removal is
   * pending confirmation, so it is always present here.
   */
  readonly artifact: ArtifactListItem;
  /**
   * Dismiss without deleting (Cancel / Escape / backdrop). On this path the
   * triggering card is still in the list, so the DS `useDialog` cleanup restores
   * focus to its Remove button for free.
   */
  readonly onClose: () => void;
  /**
   * The delete succeeded. Distinct from {@link onClose} because on success the
   * triggering card is about to be removed from the list — the parent both
   * dismisses the dialog and re-targets focus (the removed Remove button can't
   * receive it), instead of relying on `useDialog`'s restore-to-trigger.
   */
  readonly onDeleted: () => void;
}

/**
 * RemoveArtifactDialog — the S10 confirm gate for the Files rail's Remove action.
 * A DS `Modal` (focus-trapped via `useDialog`) asking to hard-delete one file;
 * on confirm it runs the {@link useDeleteArtifact} mutation
 * (`DELETE /workstreams/{id}/artifacts/{artifactId}`), which invalidates the
 * artifacts query so the removed card drops from the list.
 *
 * This is an *irreversible* action, so confirm is mandatory (the bucket is
 * unversioned — the delete is permanent). Per frontend-architecture R7 the Delete
 * trigger is disabled while the mutation is pending (the double-submit guard —
 * optimistic UI alone would not prevent a double-fire), and the dialog is made
 * non-dismissible mid-flight so Escape/backdrop can't abandon an in-progress
 * delete. A failure keeps the dialog open with an inline error affordance to retry.
 */
export function RemoveArtifactDialog({
  workstreamId,
  artifact,
  onClose,
  onDeleted,
}: RemoveArtifactDialogProps) {
  const mutation = useDeleteArtifact(workstreamId);
  const pending = mutation.isPending;

  function handleDelete() {
    mutation.mutate(artifact.artifactId, { onSuccess: onDeleted });
  }

  return (
    <Modal
      open
      onClose={onClose}
      dismissible={!pending}
      size="sm"
      title="Remove file"
      actions={
        <>
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleDelete} disabled={pending}>
            {pending ? 'Deleting…' : 'Delete'}
          </Button>
        </>
      }
    >
      <p style={message}>
        Delete <strong>{artifact.filename}</strong>? This can’t be undone.
      </p>
      {mutation.isError && (
        <div role="alert" style={errorSlot}>
          <Callout tone="danger">Couldn’t delete this file. Please try again.</Callout>
        </div>
      )}
    </Modal>
  );
}
RemoveArtifactDialog.displayName = 'RemoveArtifactDialog';
